
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { useMemo } from 'react';
import { GetListingsQueryVariables } from '@unreserved-frontend-v2/api/generated/graphql/get-listings.graphql';
import { GetListingsListResultsQuery, GetListingsListResultsQueryVariables, } from '@unreserved-frontend-v2/api/generated/graphql/get-listings-list-results.graphql';
import { GetListingsMetadataQuery, GetListingsMetadataQueryVariables, } from '@unreserved-frontend-v2/api/generated/graphql/get-listings-metadata.graphql';
import { FacetGroup, Place, ResidentialPropertyListingConnection, } from '@unreserved-frontend-v2/api/generated/graphql/types';
import { useGetListingsListResults } from '@unreserved-frontend-v2/api/hooks/use-get-listings-list-results';
import { useGetListingsMetadata } from '@unreserved-frontend-v2/api/hooks/use-get-listings-metadata';
import { useSSRBatchedData } from '@unreserved-frontend-v2/api/hooks/use-ssr-batched-data';
import { getListingsData } from '@unreserved-frontend-v2/modules/listings/api-mappings/listings';
import { ComparablesInfoBoxProps } from '@unreserved-frontend-v2/modules/listings/components/details/comparables-info/comparables-info';
import { TagType } from '@unreserved-frontend-v2/ui/tag-selector/tag-selector';
export enum ListingsBatchedDataKeys {
    LIST_RESULTS = 'listResults',
    COMPARABLES = 'comparables',
    PLACE_INFO = 'placeInfo.places',
    FACET_GROUPS = 'facetGroups.facetGroups'
}
/**
 * This hook is for primarily data management of batched SSR values combined with data from real time client-side requests
 *
 * @param connectionQueryVariables The main `GetListingsQueryVariables` value, used for both SSR batched & real-time data
 * @returns
 */
const usePageValues = (connectionQueryVariables: GetListingsQueryVariables) => {
    // Shared variables
    const { comparablesInput, input, inputPlaces, facetGroupNames } = connectionQueryVariables;
    // ======================================== Listings data setup ========================================
    /** Listings are newly requested every time anything changes anywhere */
    const { data: residentialPropertyListingConnection, isFetching, isFetchedAfterMount, } = useSSRBatchedData<GetListingsListResultsQueryVariables, {
        data: GetListingsListResultsQuery;
        isFetching: boolean;
        isFetchedAfterMount: boolean;
    }>(ListingsBatchedDataKeys.LIST_RESULTS, {
        input
    }, useGetListingsListResults);
    //======================================== Metadata data setup ========================================
    /** Metadata are newly requested every time any filters change except for pagination */
    const metadataVariables = useMemo(() => {
        return {
            facetGroupNames,
            // Filtering out pagination variables
            input: {
                ...input,
                first: 1,
                offset: 0
            },
            inputPlaces,
            comparablesInput
        };
    }, [comparablesInput, facetGroupNames, input, inputPlaces]);
    const { data: metadata, isFetching: isFetchingMetadata } = useSSRBatchedData<GetListingsMetadataQueryVariables, {
        data?: GetListingsMetadataQuery;
        isFetching: boolean;
    }>([ListingsBatchedDataKeys.FACET_GROUPS, ListingsBatchedDataKeys.COMPARABLES, ListingsBatchedDataKeys.PLACE_INFO], metadataVariables, useGetListingsMetadata);
    // Running all data through a  parser to get the appropriate fields
    const { listings, totalNumberOfListings, residentialTypes, residentialSubType, metroArea, city, region, neighbourhood, macroNeighbourhood, subNeighbourhood, street, place, tags, userListingSection, } = getListingsData({
        ...(residentialPropertyListingConnection?.listResults as ResidentialPropertyListingConnection),
        places: (metadata?.placeInfo?.places || []) as Place[],
        facetGroups: (metadata?.facetGroups?.facetGroups || []) as FacetGroup[]
    });
    return useMemo(() => ({
        isFetching: isFetching || isFetchingMetadata,
        isFetchedAfterMount,
        totalNumberOfListings,
        listings,
        residentialTypes,
        residentialSubType,
        place,
        metroArea,
        city,
        region,
        macroNeighbourhood,
        subNeighbourhood,
        neighbourhood,
        street,
        userListingSection,
        // Ensure tags match expected rendering type
        tags: tags?.facets.filter(({ id }) => !!id).map(({ id, count, name }) => ({ id, count, name } as TagType)) || [],
        listingsComparablesProps: {
            data: metadata?.comparables,
            isFetching: isFetchingMetadata
        } as Omit<ComparablesInfoBoxProps, 'place' | 'filterSelections'>
    }), [
        isFetching,
        isFetchedAfterMount,
        totalNumberOfListings,
        listings,
        residentialTypes,
        residentialSubType,
        place,
        metroArea,
        city,
        region,
        macroNeighbourhood,
        subNeighbourhood,
        neighbourhood,
        street,
        userListingSection,
        tags?.facets,
        metadata?.comparables,
        isFetchingMetadata,
    ]);
};
export default usePageValues;

    async function __Next_Translate__getServerSideProps__1955d79dfed__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: 'components//[listing]/use-page-values',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1955d79dfed__ as getServerSideProps }
  